import React from 'react';
import mapboxgl from 'mapbox-gl';
import { PageStrings } from '@alltrails/shared/utils/pageStringHelpers';
import postal from 'postal';
import turfDistance from '@turf/distance';
import TrackHoverCard from '../../../../../components/cards/TrackHoverCard';
import TrailHoverCard from '../../../../../components/cards/TrailHoverCard';
import { atBoundsToLngLatBounds, lngLatBoundsToAtBounds, lineOrSegSort } from '../../../../../utils/at_map_helpers';
import { clearCustomStyle, swapBaseStyle, initCustomStyle, createBlankStyle, setTerrain } from '../../../../../utils/mapbox/style_helpers';
import { styleConfigs } from '../../../../../utils/mapbox/style_configs';
import { getPermissionsFromContext, ableToProceed, combineACLs } from '../../../../../utils/acl_helper';
import { modalSignup } from '../../../../../utils/modal_funnel_util';
import { overlayConfigs, refreshOverlaysOnPan } from '../../../../../utils/mapbox/overlay_configs';
import { removePreviewLines } from '../../../../../utils/mapbox/overlays/preview_lines';
import { addWaypointToTrail, saveWaypointChanges, updateAtMapMetadata, removeAtMapFromTrail } from '../../../../../utils/requests/at_map_requests';
import WaypointPopup from '../../../../../components/shared/MapPopups/WaypointPopup';
import MapPopup from '../../../../../components/shared/MapPopups/MapPopup';
import MapTip from '../../../../../components/shared/MapTip';
import { updateCurrLocMarker } from '../../../../../utils/mapbox/overlays/current_location';
import { mergeTrails } from '../../../../../utils/requests/trail_requests';
import { StringConstantsMixin } from '../shared/string_constants_mixin';
import { initLayerEvents } from '../../../../../utils/mapbox/layers/layer_helpers';

const terrainEnabledLayers = ['alltrailsOutdoorsV2', 'alltrailsSatellite'];
const terrainEnabledOverlays = ['distanceMarkers', 'nearbyTrails'];
const defaultTerrainPitch = 65;

const MapMixin = {
  componentDidMount() {
    if (!this.listeners) {
      this.listeners = [];
    }
    this.listeners.push(this.props.messagingChannel.subscribe('edit.change_bounds', this.handleBoundsChange));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.change_lat_lng', this.handleLatLngChange));
  },
  componentWillUnmount() {
    this.listeners.forEach(sub => {
      postal.unsubscribe(sub);
    });
  },
  initMap(isPrint) {
    if (!mapboxgl.supported()) {
      alert(StringConstantsMixin.strings.MAPBOXGL_NOT_SUPPORTED);
      return;
    }

    const { context, initialCenter, initialBounds, setPrintFlags } = this.props;

    let initBounds;
    let initCenter;
    let initZoom = 14;

    if (isPrint) {
      const { mapZoom } = this.props;
      // LatLng -> LngLat
      initCenter = [parseFloat(initialCenter[1]), parseFloat(initialCenter[0])];
      if (mapZoom) {
        initZoom = parseFloat(mapZoom);
      } else {
        initZoom = parseFloat(initialCenter[2]);
      }
    } else if (initialBounds) {
      initBounds = atBoundsToLngLatBounds(initialBounds);
    } else if (initialCenter) {
      // LatLng -> LngLat
      initCenter = [parseFloat(initialCenter[1]), parseFloat(initialCenter[0])];
      initZoom = parseFloat(initialCenter[2]);
    }

    const preserveDrawingBuffer = !!setPrintFlags;

    // Init mapbox map
    mapboxgl.accessToken = this.props.mapboxAccessToken;
    const displayMetric = this.props.context.displayMetric;
    const map = new mapboxgl.Map({
      bounds: initBounds,
      center: initCenter,
      container: this.props.mapDivId,
      fadeDuration: 0,
      preserveDrawingBuffer,
      style: createBlankStyle(),
      zoom: initZoom,
      maxPitch: 60
    });

    // Add custom AllTrails properties onto map
    map.context = this.props.context;
    initCustomStyle(map);
    initLayerEvents(map);
    // Add mapbox map onto component
    this.mbMap = map;
    if (this.props.setMapRef) {
      this.props.setMapRef(map);
    }

    // Switch to selected style
    const styleConfig = styleConfigs[this.state.currentMapLayer];
    styleConfig.loadStyle(context.locale, context.displayMetric).then(style => {
      swapBaseStyle(map, style, false);
    });

    map.once('styledata', () => {
      // do not display map until the style has loaded fully, otherwise adding layers will break the map view
      this.setState({ initialStyleLoaded: true });
    });

    // Configure map controls
    map.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 80,
        unit: displayMetric ? 'metric' : 'imperial'
      }),
      'bottom-right'
    );
    map.geolocateControl = new mapboxgl.GeolocateControl({});
    map.addControl(map.geolocateControl, 'bottom-right');
    map.boxZoom.disable();

    map.on('click', e => {
      // Prevent click from being fired on "mouseup" at end of shift drag event
      if (map.shiftDragging) {
        return;
      }
      // defocus any text fields when the map is clicked/touched
      $('input').blur();
      this.props.messagingChannel.publish('map.click', e);
    });
    map.on('moveend', () => {
      try {
        this.props.messagingChannel.publish('map.bounds.changed', {
          center: this.getMapCenter(),
          bounds: this.getMapBounds(),
          zoom: this.getMapZoom()
        });
        refreshOverlaysOnPan(this.mbMap, this.getOverlayConfigParams());
      } catch (error) {
        // Every time we make the initial switch from 2D to 3D this.getMapBounds() throws
        // an unexplained "Invalid LngLat object: (NaN, NaN)" error deep within mapbox-gl
      }
    });
    map.on('rotate', () => {
      const compassRotation = this.mbMap.getBearing() * -1;
      this.setState({ compassRotation });
    });
    map.once('load', () => {
      this.setState({ mapLoaded: true });
    });
    this.addShiftDragTracking();

    if (!this.keysDown) {
      this.keysDown = {}; // hash of key codes that are pressed-down, used to prevent firing keydown events multiple times on PCs
    }
    document.addEventListener('keyup', this.keyUpDown);
    document.addEventListener('keydown', this.keyUpDown);

    return map;
  },
  addShiftDragTracking(shiftDragThresh = 5) {
    const map = this.mbMap;

    // threshold in meters
    // Do nothing if mapbox box zoom is enabled
    if (map.boxZoom.isEnabled()) {
      return;
    }
    // Implement shift-drag event tracking
    map.shiftDragging = false;
    map.shiftDraggingLastLngLat = null;

    // Functionality should continue in shift-drag mode if shift key is released,
    // but exit mode ASAP on mouseup
    map.on('mousedown', e => {
      map.shiftDragging = map.shiftKeyDown;
      if (!map.shiftDragging) {
        return;
      }
      e.preventDefault(); // prevent map pane dragging
      map.shiftDraggingLastLngLat = e.lngLat;
      this.props.messagingChannel.publish('map.shift_drag.start', e);
    });
    map.on('mouseup', e => {
      if (!map.shiftDragging) {
        return;
      }
      this.props.messagingChannel.publish('map.shift_drag.end', e);
      map.shiftDragging = false;
    });
    map.on('mousemove', e => {
      if (!map.shiftDragging) {
        return;
      }
      const lngLat = e.lngLat;
      if (map.shiftDraggingLastLngLat) {
        const d = turfDistance(map.shiftDraggingLastLngLat.toArray(), lngLat.toArray()) * 1000;
        if (d < shiftDragThresh) {
          return;
        }
      }
      map.shiftDraggingLastLngLat = lngLat;
      this.props.messagingChannel.publish('map.shift_drag.move', e);
    });
  },
  keyUpDown(e) {
    // e.keyCode: Backspace=8, Shift=16, y|Y=89, z|Z=90

    const keyDown = e.type === 'keydown';
    const cmdKey = e.metaKey || e.ctrlKey;
    const shiftKey = e.shiftKey;

    // Update shiftKeyDown modifier status
    this.mbMap.shiftKeyDown = shiftKey;

    // Ignore multiple firings of keydown events on modifier keys (happens when holding key down on PC)
    // by keeping track of what keys are down using keysDown hash.
    // Note: Normal letter keys SHOULD fire keydown events when held down, regardless of platform.
    // Only track for shift, ctrl, and opt/alt keys
    if (e.keyCode >= 16 && e.keyCode <= 18) {
      if (keyDown) {
        if (this.keysDown[e.keyCode]) {
          return; // ignore keydown event
        }
        this.keysDown[e.keyCode] = true;
      } else if (this.keysDown[e.keyCode]) {
        delete this.keysDown[e.keyCode];
      }
    }

    if (keyDown && cmdKey && e.keyCode == 90 && !shiftKey) {
      // cmd-z
      this.props.messagingChannel.publish('button.click.undo');
    } else if (keyDown && cmdKey && (e.keyCode == 89 || (e.keyCode == 90 && shiftKey))) {
      // cmd-y or cmd-shift-z
      this.props.messagingChannel.publish('button.click.redo');
    } else if (e.keyCode == 16) {
      // shift
      this.props.messagingChannel.publish('map.shift_key', keyDown);
    } else if (e.keyCode == 18) {
      // alt/opt
      this.props.messagingChannel.publish('map.opt_key', keyDown);
    } else if (e.keyCode == 17) {
      // ctrl
      this.props.messagingChannel.publish('map.ctrl_key', keyDown);
      return; // don't prevent default
    } else if (e.keyCode == 8 || e.keyCode == 46 || e.keyCode == 51) {
      // backspace or delete keys
      this.props.messagingChannel.publish('map.del_key', keyDown);
      return; // don't prevent default
    } else {
      return; // don't prevent default
    }

    // Prevent default key functionality
    e.preventDefault();
  },
  clearMap() {
    this.disableAllOverlays();
    clearCustomStyle(this.mbMap);
  },
  handleCompassClick() {
    const pitch = this.state.terrainActive ? defaultTerrainPitch : 0;
    this.mbMap.easeTo({ bearing: 0, pitch });
  },
  compassClick() {
    if (this.mbMap.getBearing() !== 0 || this.mbMap.getPitch() !== 0) {
      this.reorientUser();
    } else {
      this.locateUser();
    }
  },
  reorientUser() {
    this.mbMap.easeTo({ bearing: 0, pitch: 0 });
  },
  locateUser() {
    this.mbMap.geolocateControl.trigger();
  },
  getMapCenter() {
    return this.mbMap
      .getCenter()
      .toArray()
      .reverse();
  },
  getMapBounds() {
    // Return bounds in format [N, S, W, E]
    const bounds = this.mbMap.getBounds();
    return [bounds.getNorth(), bounds.getSouth(), bounds.getWest(), bounds.getEast()];
  },
  getMapAtBounds() {
    const bounds = this.mbMap.getBounds();
    return lngLatBoundsToAtBounds(bounds);
  },
  getMapZoom() {
    return this.mbMap.getZoom();
  },
  getMaxZoom(locationType) {
    switch (locationType) {
      case 'area':
      case 'cityo':
        return 12;
      case 'state':
        return 11;
      case 'country':
        return 10; // Ideal size for small countries like Malta and Lichtenstein
      default:
        return 12; // Using original default zoom
    }
  },
  enable3D() {
    if (!this.props.context.currentUser) {
      const returnToUrl = window.checkLinkAndAppendLanguage(window.location.pathname + window.location.search);
      modalSignup('map-terrain-3d', returnToUrl);
      return;
    }

    this.disableNonTerrainEnabledOverlays();

    this.setState({ terrainActive: true });

    setTerrain(this.mbMap, true);

    // set fog
    this.mbMap.setFog({
      range: [2, 12],
      color: 'white',
      'horizon-blend': 0.1
    });

    this.mbMap.setMaxPitch(85);
    this.mbMap.easeTo({ pitch: defaultTerrainPitch });
  },
  disable3D(resetNorthPitch = true) {
    this.setState({
      terrainActive: false,
      compassRotation: resetNorthPitch ? 0 : this.state.compassRotation
    });

    if (resetNorthPitch) {
      this.mbMap.resetNorthPitch();
    }
    setTerrain(this.mbMap, false);
  },
  zoomInClicked() {
    this.mbMap.zoomIn();
  },
  zoomOutClicked() {
    this.mbMap.zoomOut();
  },
  setMapZoom(zoom) {
    this.mbMap.setZoom(zoom);
  },
  setMapBounds(bounds) {
    // [w, s, e, n]
    this.mbMap.fitBounds([bounds.longitudeTopLeft, bounds.latitudeBottomRight, bounds.longitudeBottomRight, bounds.latitudeTopLeft]);
  },
  setMapCenterAndZoom(latitude, longitude, zoom) {
    this.mbMap.jumpTo({ center: [longitude, latitude], zoom });
  },
  handleBoundsChange(data) {
    this.setMapBounds(data.bounds, data.options);
  },
  handleLatLngChange(coordinates) {
    const zoom = coordinates.length == 3 ? coordinates[2] : 15;
    this.setMapCenterAndZoom(coordinates[0], coordinates[1], zoom, { suppressBoundsCallback: true });
  },
  handleTerrainMapTipCloseClicked() {
    this.setState({ terrainMapTipClosed: true });
  },
  toggleExpanded() {
    this.setState({ expanded: !this.state.expanded });
  },
  toggleElevationCollapsed() {
    this.setState({ elevationCollapsed: !this.state.elevationCollapsed });
  },
  handleElevationPaneMouseOver(lngLat) {
    updateCurrLocMarker(this.mbMap, lngLat);
  },
  handleCardClick(card) {
    if (card.layerType === 'basemap') {
      this.setLayer(card.key);
    } else {
      this.toggleOverlay(card.key);
    }
  },
  handleHeatmapOverlayClick(overlay) {
    this.toggleOverlay(overlay);
  },
  setLayer(layer) {
    const { context } = this.props;
    // Do nothing if layer has already been selected
    if (layer === this.state.currentMapLayer) {
      return;
    }
    _gaq.push(['_trackEvent', 'Track Show', 'Map', 'Select Layer']);
    const styleConfig = styleConfigs[layer];
    const mapLayerRef = `map-layer-${layer}`;
    const returnToUrl = window.location.pathname + window.location.search;
    removePreviewLines(this.mbMap);
    // Show signup modal if no logged-in user
    if (!context.currentUser) {
      modalSignup(mapLayerRef, returnToUrl);
      return;
    }
    // Do nothing if doesn't have proper permissions to view/enable
    const combinedACL = combineACLs([styleConfig.aclEnabled, styleConfig.aclVisible]);
    if (!ableToProceed(combinedACL, this.getPermissions(), mapLayerRef, returnToUrl)) {
      return;
    }
    // Swap base style of map
    styleConfig
      .loadStyle(context.locale, context.displayMetric, this.state.adminCustomizationSettings)
      .then(style => {
        swapBaseStyle(this.mbMap, style, this.state.terrainActive);
      })
      .catch(() => {
        console.error(`Layer ${layer} is not available`);
        return;
      });

    const currentLayerTerrainEnabled = terrainEnabledLayers.includes(layer);

    if (this.state.terrainActive && !currentLayerTerrainEnabled) {
      this.disable3D();
    }

    this.setState({
      currentMapLayer: layer,
      currentLayerTerrainEnabled
    });
  },
  getOverlaysChangedCb() {
    return this.printMap ? this.printMap.updatePermalink : null;
  },
  toggleOverlay(overlay) {
    _gaq.push(['_trackEvent', 'Track Show', 'Map', 'Select Overlay']);
    const overlayConfig = overlayConfigs[overlay];
    // Do nothing if doesn't have proper permissions to view/enable
    const combinedACL = combineACLs([overlayConfig.aclEnabled, overlayConfig.aclVisible]);
    const mapOverlayRef = `map-overlay-${overlay}`;
    const returnToUrl = window.location.pathname + window.location.search;
    if (!ableToProceed(combinedACL, this.getPermissions(), mapOverlayRef, returnToUrl)) {
      return;
    }
    // Toggle the display of an overlay
    let enabledOverlays = this.state.enabledOverlays;
    const configParams = this.getOverlayConfigParams();
    const isEnabled = enabledOverlays.includes(overlay);
    if (isEnabled) {
      // disable
      enabledOverlays = enabledOverlays.filter(o => o !== overlay);
      overlayConfig.remove(this.mbMap, { ...configParams, enabledOverlays });
      // disable corresponding heatmap overlay too
      if (overlayConfig.hasHeatmap) {
        enabledOverlays = enabledOverlays.filter(o => o !== `${overlay}Heatmap`);
        overlayConfigs[`${overlay}Heatmap`].remove(this.mbMap, { ...configParams, enabledOverlays });
      }
    } else {
      // enable
      enabledOverlays = [...enabledOverlays, overlay];
      overlayConfig.add(this.mbMap, { ...configParams, enabledOverlays });
    }
    this.setState({ enabledOverlays }, this.getOverlaysChangedCb());
    // Notify parent of overlay change (currently only used for switching on/off the nearby trails toggle)
    if (this.props.handleOverlayToggled) {
      this.props.handleOverlayToggled(overlay);
    }
  },
  refreshOverlay(overlay, resultsDataOverrides = {}) {
    if (!this.state.enabledOverlays.includes(overlay)) {
      return;
    }
    const overlayConfigParams = { ...this.getOverlayConfigParams(), resultsDataOverrides };
    overlayConfigs[overlay].add(this.mbMap, overlayConfigParams);
  },
  refreshTrackOverlays(resultsDataOverrides) {
    ['heatmap', 'recordingsAll', 'recordingsPop'].forEach(overlay => this.refreshOverlay(overlay, resultsDataOverrides));
  },
  getOverlayConfigParams() {
    const { exploreMap, profilePhoto, plannerMap, results, context } = this.props;
    const { enabledOverlays, selectedFilterActivity, adminCustomizationSettings } = this.state;
    const adminUser = context && context.adminUser;
    return {
      enabledOverlays,
      exploreMap,
      profilePhoto,
      handlePhotoClick: this.handlePhotoClick,
      plannerMap,
      renderHoverCard: this.renderHoverCard,
      renderWaypointPopup: this.renderWaypointPopup,
      results,
      trailId: exploreMap ? exploreMap.trailId : null,
      selectedFilterActivity,
      adminCustomizationSettings,
      adminUser
    };
  },
  getPermissions() {
    const permissions = getPermissionsFromContext(this.props.context);
    if (this.props.exploreMap || this.props.plannerMap) {
      permissions.push('exploreMap');
      if (this.props.exploreMap && this.props.exploreMap.trailId) {
        permissions.push('attachedToTrail');
      }
    }
    if (this.props.currentPage === PageStrings.CONTRIBUTE_TRAIL) {
      permissions.push('contributeTrail');
    }
    if (this.props.currentPage !== PageStrings.EXPLORE_ALL_PAGE) {
      permissions.push('nearbyTrails');
    }
    this.state.enabledOverlays.forEach(overlay => {
      permissions.push(`${overlay}Enabled`);
    });
    return permissions;
  },
  disableNonTerrainEnabledOverlays() {
    const enabledOverlays = [];
    this.state.enabledOverlays.forEach(overlay => {
      if (terrainEnabledOverlays.includes(overlay)) {
        enabledOverlays.push(overlay);
      } else {
        const overlayConfig = overlayConfigs[overlay];
        overlayConfig.remove(this.mbMap);
      }
    });
    this.setState({ enabledOverlays }, this.getOverlaysChangedCb());
  },
  disableAllOverlays() {
    this.state.enabledOverlays.forEach(overlay => {
      const overlayConfig = overlayConfigs[overlay];
      overlayConfig.remove(this.mbMap);
    });
    this.setLayer('alltrailsOutdoorsV2');
    this.setState({ enabledOverlays: [] }, this.getOverlaysChangedCb());
  },
  enableInitialOverlays(overlays) {
    const enabledOverlays = [...this.state.enabledOverlays];
    const overlayConfigParams = this.getOverlayConfigParams();
    overlays.forEach(overlay => {
      if (!enabledOverlays.includes(overlay)) {
        const overlayConfig = overlayConfigs[overlay];
        overlayConfig.add(this.mbMap, overlayConfigParams);
        enabledOverlays.push(overlay);
      }
    });
    this.setState({ enabledOverlays }, this.getOverlaysChangedCb());
  },
  renderHoverCard(result) {
    if (result.type === 'trail') {
      return this.renderTrailHoverCard(result);
    }
    if (result.type === 'track' || result.type === 'map') {
      return this.renderTrackHoverCard(result);
    }
    return null;
  },
  renderTrailHoverCard(result) {
    const { listMethods, resultCardFunctions } = this.props;
    return (
      <TrailHoverCard
        allowClickingCompletedBadge={
          resultCardFunctions && resultCardFunctions.allowClickingCompletedBadge
            ? resultCardFunctions.allowClickingCompletedBadge(result.type, result.ID)
            : undefined
        }
        bottomButtonsConfig={this.createAdminTrailLinks(result)}
        context={this.props.context}
        handleCardClick={resultCardFunctions && resultCardFunctions.handleTrailClick ? () => resultCardFunctions.handleTrailClick(result) : undefined}
        handleCompletedBadgeClick={
          resultCardFunctions && resultCardFunctions.handleCompletedBadgeClick
            ? () => resultCardFunctions.handleCompletedBadgeClick(result.type, result.ID)
            : undefined
        }
        handleFavoriteClick={
          listMethods && listMethods.handleFavoriteClick ? () => listMethods.handleFavoriteClick(result.type, result.ID, result.objectID) : undefined
        }
        handleLocationClick={resultCardFunctions && resultCardFunctions.handleLocationClick ? resultCardFunctions.handleLocationClick : undefined}
        isCompleted={listMethods && listMethods.isComplete ? listMethods.isComplete(result.type, result.ID) : undefined}
        isFavorite={listMethods && listMethods.isFavorite ? listMethods.isFavorite(result.type, result.ID) : undefined}
        isMobileWidth={this.props.isMobileWidth}
        isVerified={listMethods && listMethods.isVerified ? listMethods.isVerified(result.type, result.ID) : undefined}
        key={`trail-${result.ID}`}
        showFavoriteHeart={listMethods && listMethods.showFavoriteHeart}
        trail={result}
        trailUrl={resultCardFunctions && resultCardFunctions.formatTrailUrl ? resultCardFunctions.formatTrailUrl(result) : undefined}
      />
    );
  },
  renderTrackHoverCard(result) {
    result = { ...result };
    // need to fully deserialize some props (which are partially serialized when converted to a mapbox feature property)
    if (typeof result.user === 'string') {
      result.user = JSON.parse(result.user);
    }
    if (typeof result.activity === 'string') {
      result.activity = JSON.parse(result.activity);
    }
    if (typeof result.activities === 'string') {
      result.activities = JSON.parse(result.activities);
    }
    result.timezone = result.timezone === 'null' ? null : result.timezone;

    const { resultCardFunctions, listMethods } = this.props;
    let handleCardClick;
    if (resultCardFunctions) {
      if (result.type === 'track' && resultCardFunctions.handleTrackClick) {
        handleCardClick = () => resultCardFunctions.handleTrackClick(result);
      } else if (resultCardFunctions.handleMapClick) {
        handleCardClick = () => resultCardFunctions.handleMapClick(result);
      }
    }

    return (
      <TrackHoverCard
        bottomButtonsConfig={this.createAdminTrackLinks(result)}
        context={this.props.context}
        handleCardClick={handleCardClick}
        handleFavoriteClick={
          listMethods && listMethods.handleFavoriteClick ? () => listMethods.handleFavoriteClick(result.type, result.ID) : undefined
        }
        isFavorite={listMethods && listMethods.isFavorite ? listMethods.isFavorite(result.type, result.ID) : undefined}
        isMobileWidth={this.props.isMobileWidth}
        key={`track-${result.ID}`}
        showFavoriteHeart={listMethods && listMethods.showFavoriteHeart}
        track={result}
      />
    );
  },
  renderTerrainTip() {
    const { terrainActive, terrainMapTipClosed, currentlyEditingRoute } = this.state;
    const { TITLE, TEXT, LEARN_MORE } = StringConstantsMixin.strings.MAP_TIPS.TERRAIN;
    const faqUrlLocale =
      {
        'en-US': 'en-us',
        'fr-FR': 'fr',
        'es-ES': 'es',
        'de-DE': 'de'
      }[this.props.context.languageRegionCode] || 'en-us';
    const faqUrl = `https://support.alltrails.com/hc/${faqUrlLocale}/articles/360058630271-How-do-I-view-AllTrails-maps-in-3D-`;

    if (terrainMapTipClosed || !terrainActive || currentlyEditingRoute) {
      // The Smart Routing map tip obstructs the 3D map tip, so just don't show it in this case
      return null;
    }

    return <MapTip title={TITLE} text={TEXT} onClose={this.handleTerrainMapTipCloseClicked} linkUrl={faqUrl} linkText={LEARN_MORE} />;
  },
  createAdminTrailLinks(trail) {
    const { context, selectedObject } = this.props;
    if (!context || !context.adminUser) {
      return null;
    }
    const linksConfig = [];
    if (selectedObject && selectedObject.type === 'trail' && trail.ID !== selectedObject.ID) {
      linksConfig.push({
        label: 'Merge',
        labelInProgress: 'Merging...',
        labelSuccess: 'Merged',
        labelFailed: 'Failed',
        handleClick: () => {
          return this.trailMerge(selectedObject, trail);
        }
      });
    }
    return linksConfig;
  },

  trailMerge(mergeIntoTrail, mergeTrail) {
    let handleMergeSuccess;
    if (!window.confirm(`Merge ${mergeTrail.name} into ${mergeIntoTrail.name}?`)) {
      // Check if the admin wants to merge the trails in the reverse direction
      [mergeTrail, mergeIntoTrail] = [mergeIntoTrail, mergeTrail];
      if (!window.confirm(`Merge ${mergeTrail.name} into ${mergeIntoTrail.name}?`)) {
        return Promise.reject();
      }
      handleMergeSuccess = () => {
        // redirect to merge-into-trail's contribute page
        window.location.href = `/contribute/route?trail_id=${mergeIntoTrail.ID}`;
      };
    }
    const mergeTrailsPromise = mergeTrails(mergeIntoTrail, mergeTrail);
    if (handleMergeSuccess) {
      mergeTrailsPromise.then(handleMergeSuccess);
    }
    return mergeTrailsPromise;
  },

  createAdminTrackLinks(track) {
    const { context, exploreMap } = this.props;
    if (!context || !context.adminUser) {
      return null;
    }
    const linksConfig = [];
    if (exploreMap && exploreMap.trailId) {
      const trackTrailId = track.trailId || track.trail_id;
      if (trackTrailId === exploreMap.trailId) {
        linksConfig.push({
          label: 'Remove',
          labelInProgress: 'Removing...',
          labelSuccess: 'Removed',
          labelFailed: 'Failed',
          handleClick: () => {
            // Return promise before chaining .then
            const promise = removeAtMapFromTrail(track.ID, exploreMap.trailId);
            promise.then(() => this.refreshOverlay('heatmap'));
            return promise;
          }
        });
      } else if (!track.private) {
        linksConfig.push({
          label: 'Attach to Trail',
          labelInProgress: 'Attaching...',
          labelSuccess: 'Attached',
          labelFailed: 'Failed',
          handleClick: () => {
            // Return promise before chaining .then
            const promise = updateAtMapMetadata(track.ID, { trail_id: exploreMap.trailId });
            // Update refreshed data with this change, since Algolia might not yet reflect it
            const resultsDataOverrides = {
              [track.ID]: { trail_id: exploreMap.trailId }
            };
            promise.then(() => this.refreshTrackOverlays(resultsDataOverrides));
            return promise;
          }
        });
      }
    }
    if (!track.hidden) {
      linksConfig.push({
        label: 'Hide',
        labelInProgress: 'Hiding...',
        labelSuccess: 'Hidden',
        labelFailed: 'Failed',
        handleClick: () => {
          return updateAtMapMetadata(track.ID, { hidden: true });
        }
      });
    }
    if (!track.private) {
      linksConfig.push({
        label: 'Add as New Trail',
        handleClick() {
          window.open(`/trail/new?recording=${track.ID}`, '_blank');
        }
      });
    }
    return linksConfig;
  },
  getRouteName(id) {
    const { routes } = this.props.exploreMap;

    if (!routes || routes.length <= 1) {
      return StringConstantsMixin.strings.TRACK_DETAIL.ROUTE;
    }

    const orderNum = routes.sort(lineOrSegSort).findIndex(route => {
      return route.id === id;
    });

    return StringConstantsMixin.strings.MAP_CREATOR.ROUTE_WITH_NUMBER(orderNum + 1);
  },
  renderRoutePopup(data) {
    const name = this.getRouteName(data.lineId);
    return <MapPopup name={name} />;
  },
  existingVRWaypoint(waypoint) {
    return this.props.exploreMap.waypoints.find(wp => {
      return waypoint.location.latitude === wp.location.latitude && waypoint.location.longitude === wp.location.longitude;
    });
  },
  renderWaypointPopup(waypoint, isCommunityContent) {
    waypoint = { ...waypoint };
    let toggleShowTitle;
    let addToVRoute;
    let messagingChannel;
    // need to fully deserialize waypoint (which is partially serialized when converted to a mapbox property)
    if (typeof waypoint.location === 'string') {
      waypoint.location = JSON.parse(waypoint.location);
    }
    if (typeof waypoint.waypointDisplayProperty === 'string') {
      waypoint.waypointDisplayProperty = JSON.parse(waypoint.waypointDisplayProperty);
    }

    const {
      context: { currentUser, adminUser },
      exploreMap,
      selectedObject
    } = this.props;

    if (this.props.messagingChannel && exploreMap.waypointEditIndex !== null && waypoint.id === exploreMap.editWaypointId) {
      messagingChannel = this.props.messagingChannel;
    }

    if (
      !isCommunityContent && // don't allow toggle of titles on community waypoints overlay
      currentUser &&
      exploreMap &&
      (adminUser || (exploreMap.user && exploreMap.user.id === currentUser.id)) &&
      selectedObject &&
      selectedObject.type !== 'trail' // don't allow waypoint titles to be shown/hidden on trail pages
    ) {
      toggleShowTitle = () => {
        const showTitle = waypoint.waypointDisplayProperty ? waypoint.waypointDisplayProperty.showTitle : false;
        saveWaypointChanges(exploreMap.id, waypoint.id, { showTitle: !showTitle }).then(updatedWaypoint => {
          this.props.messagingChannel.publish('waypoint.updated', updatedWaypoint);
        });
      };
    }

    // In an ideal world, we could simply compare the waypoint's at_map_id against exploreMap.id to see if the waypoint
    // belongs to the trail/map, but we can't do that for most of our older trails which use a separate verified
    // markers map to keep track of markers/waypoints. This isCommunityContent flag simplifies deciding whether or
    // not to show this button. Also should only show if looking at a trail's verified route.
    if (adminUser && selectedObject && selectedObject.type === 'trail') {
      if (isCommunityContent && !this.existingVRWaypoint(waypoint)) {
        addToVRoute = () => {
          return addWaypointToTrail(exploreMap.trailId, waypoint)
            .then(newWaypoint => {
              if (this.props.handleWaypointAdded) {
                this.props.handleWaypointAdded(newWaypoint);
              }

              return 'Added';
            })
            .catch(() => {
              return 'Failed';
            });
        };
      } else {
        // TODO: Remove from VRoute - need new API endpoint to do this
      }
    }

    return <WaypointPopup waypoint={waypoint} toggleShowTitle={toggleShowTitle} addToVRoute={addToVRoute} messagingChannel={messagingChannel} />;
  }
};

export { MapMixin };
